import { cn } from "@/lib/utils"
import claudeMark from "@/assets/logos/agents/claude.svg"
import codexMark from "@/assets/logos/agents/codex.svg"
import cursorMark from "@/assets/logos/agents/cursor.svg"

export type OnboardingWorkMode = "agent" | "app"

type Plan = { name: string; price: string; unit: string; detail: string }

/**
 * The New Entry Plan (07-App-v2.md, decision 14). Display only: checkout for
 * Agents is a backend ask (SUPENG-341). The free 25,000 applies to both.
 */
const PLANS: Record<OnboardingWorkMode, Plan[]> = {
  agent: [
    {
      name: "Agents",
      price: "$39",
      unit: "one time",
      detail: "100K data points for your agent, API and MCP. Unlimited sites. Run out? Top up 100K for $29. Top-ups last 12 months.",
    },
  ],
  app: [
    {
      name: "Business",
      price: "$79",
      unit: "/month",
      detail: "500K data points a month. 3 sites. Pay yearly and get 2 months free.",
    },
    {
      name: "Scale",
      price: "$279",
      unit: "/month",
      detail: "1M data points a month. Unlimited sites, 5 seats. Extra 1M for $39.",
    },
  ],
}

const FOOTNOTE: Record<OnboardingWorkMode, string> = {
  agent:
    "Agents gives your agent, the API and MCP. Want dashboards for your team? Pick In the Humblytics app. API calls are not metered.",
  app: "Business and Scale include the full dashboard, plus your agent and the API. API calls are not metered. Need more? Custom plans from $1,500.",
}

const OPTIONS: { id: OnboardingWorkMode; title: string; body: string; plan: string }[] = [
  {
    id: "agent",
    title: "With your agent",
    body: "Claude, ChatGPT, Codex or Cursor reads your numbers, builds reports and runs tests when you allow it.",
    plan: "Agents plan · $39 one time",
  },
  {
    id: "app",
    title: "In the Humblytics app",
    body: "Dashboards, heatmaps, funnels and experiments for your team. Connect an agent any time.",
    plan: "Business or Scale · from $79/month",
  },
]

/** Duotone bars, the sidebar's Analytics glyph (Paper 08, decision 15). */
function AppGlyph() {
  return (
    <svg width="22" height="22" viewBox="0 0 20 20" aria-hidden>
      <rect x="3" y="11" width="3.5" height="6" rx="1.2" fill="#D4D4D4" />
      <rect x="8.25" y="7" width="3.5" height="10" rx="1.2" fill="#8A8A8A" />
      <rect x="13.5" y="3" width="3.5" height="14" rx="1.2" fill="#A3A3A3" />
    </svg>
  )
}

type Props = {
  value: OnboardingWorkMode | null
  onChange: (mode: OnboardingWorkMode) => void
  className?: string
}

/**
 * Step 02's first question (Paper 08, 02 / 02b): agent or app. It sets the plan
 * type, so the price strip shows only that type's plans.
 */
export function OnboardingWorkModeChoice({ value, onChange, className }: Props) {
  const plans = value ? PLANS[value] : null

  return (
    <div className={cn("space-y-5", className)}>
      <div role="radiogroup" aria-label="How do you want to use Humblytics?" className="grid gap-3 sm:grid-cols-2">
        {OPTIONS.map((o) => {
          const selected = value === o.id
          return (
            <button
              key={o.id}
              type="button"
              role="radio"
              aria-checked={selected}
              onClick={() => onChange(o.id)}
              className={cn(
                "group flex flex-col gap-3 rounded-xl border bg-card p-4 text-left transition-colors",
                "outline-none focus-visible:border-ring focus-visible:ring-[2px] focus-visible:ring-ring/30",
                selected ? "border-primary/50 shadow-[0_1px_2px_rgba(0,0,0,0.04)]" : "border-border/60 hover:border-border hover:bg-muted/30",
              )}
            >
              <span className="flex items-start justify-between">
                <span className="flex h-6 items-center gap-1.5">
                  {o.id === "agent" ? (
                    [claudeMark, codexMark, cursorMark].map((src) => (
                      <img key={src} src={src} alt="" className="size-[18px] dark:invert" />
                    ))
                  ) : (
                    <AppGlyph />
                  )}
                </span>
                <span
                  aria-hidden
                  className={cn(
                    "flex size-[18px] shrink-0 items-center justify-center rounded-full border transition-colors",
                    selected ? "border-primary" : "border-border group-hover:border-foreground/40",
                  )}
                >
                  <span className={cn("size-2 rounded-full bg-primary transition-opacity", selected ? "opacity-100" : "opacity-0")} />
                </span>
              </span>
              <span className="space-y-1">
                <span className="block text-sm font-medium text-foreground">{o.title}</span>
                <span className="block text-xs leading-[18px] text-muted-foreground">{o.body}</span>
              </span>
              <span className="text-[11px] text-muted-foreground">{o.plan}</span>
            </button>
          )
        })}
      </div>

      {plans && value ? (
        <section aria-label="What it costs" className="space-y-2">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <p className="text-xs font-medium text-foreground">
              {value === "agent" ? "What it costs with your agent" : "What it costs in the app"}
            </p>
            <p className="text-[11px] text-muted-foreground">
              Your first 25,000 data points are free. Questions you or your agent ask are not counted.
            </p>
          </div>
          <div className={cn("grid gap-2", plans.length > 1 && "sm:grid-cols-2")}>
            {plans.map((p) => (
              <div key={p.name} className="rounded-lg border border-border/60 bg-card px-4 py-3">
                <p className="text-xs font-medium text-foreground">{p.name}</p>
                <p className="mt-1 text-foreground">
                  <span className="text-xl font-semibold tabular-nums">{p.price}</span>{" "}
                  <span className="text-xs text-muted-foreground">{p.unit}</span>
                </p>
                <p className="mt-1 text-[11px] leading-[17px] text-muted-foreground">{p.detail}</p>
              </div>
            ))}
          </div>
          <p className="text-[11px] leading-[17px] text-muted-foreground">{FOOTNOTE[value]}</p>
        </section>
      ) : null}
    </div>
  )
}
